import { useContext } from 'react';
import PropTypes from 'prop-types';
import { ActionButton } from 'uikit/ActionButton/ActionButton';
import { Status } from 'uikit/Status/Status';
import i18nContext from 'components/i18n-context';
import { convertUTCToTransactionsContextTimeZone } from 'components/utils';
import { amountFormattedValue, formatAccountNumber, isCryptoProvider } from 'services/utils';
import { TRANSACTIONS_TABLE_STATUS, TRANSACTION_TYPES, TYPES_OF_TRANSACTION } from 'components/common/constants';
import { ReactComponent as CalendarIcon } from 'assets/calendar-icon.svg';
import { ReactComponent as StatusIcon } from 'assets/mobile-transaciton-icon.svg';
import { ReactComponent as ProfileIcon } from 'assets/iconButtons/profile.svg';
import { ReactComponent as WalletIcon } from 'assets/wallet.svg';
import './LatestTransactions.scss';

export const TransactionItem = ({ transaction, onInfoClick, isMobile, isCryptoTransactions }) => {
  const i18n = useContext(i18nContext);
  const createdAt = convertUTCToTransactionsContextTimeZone(transaction.created_at);
  const isIncomingReturnType = TRANSACTION_TYPES.INCOMING === transaction.type;
  const amount = `${isIncomingReturnType ? '+' : '-'} ${
    isIncomingReturnType && transaction.status !== TRANSACTIONS_TABLE_STATUS.failed
      ? transaction.sourceAmountDetails?.displayAmount || amountFormattedValue(transaction.source_amount, 2)
      : transaction.amountDetails?.displayAmount || amountFormattedValue(transaction.amount, 2)
  }`;
  const transactionStatus = Object.keys(TRANSACTIONS_TABLE_STATUS).find(
    (key) => TRANSACTIONS_TABLE_STATUS[key] === transaction.status
  );

  if (isMobile) {
    return (
      <div className={'transaction-item-mobile'}>
        <div className={'item-mobile-top'}>
          <div className={'item-mobile-date'}>
            <CalendarIcon className={'icon'} />
            <p>{`${createdAt.date} ${createdAt.time}`}</p>
          </div>
          <div className={'item-mobile-status'}>
            <StatusIcon className={'icon'} />
            <Status color={transactionStatus} status={i18n.getMessage(`transaction.status.${transactionStatus}`)} />
          </div>
        </div>
        <div className={'item-mobile-from-to'}>
          <ProfileIcon className={'icon'} />
          {isCryptoProvider(transaction.transfer_type) ? (
            <div>
              <p className={'iban-text'}>{formatAccountNumber(transaction?.counterparty?.crypto_wallet)}</p>
            </div>
          ) : (
            <div>
              <p>{transaction.counterparty?.name}</p>
              <p>{formatAccountNumber(transaction.counterparty?.iban || transaction.counterparty?.number)}</p>
            </div>
          )}
        </div>
        <div className={'item-mobile-amount'}>
          <WalletIcon className={'icon'} />
          <p className={`item-amount-text ${transaction?.type}`}>{amount}</p>
          <ActionButton onClick={onInfoClick} />
        </div>
      </div>
    );
  }

  return (
    <tr className={'transaction-item'}>
      <td className={'transaction-date'}>
        <p>{createdAt.date}</p>
        <p>{createdAt.time}</p>
      </td>
      <td className={'transaction-status'}>
        <Status color={transactionStatus} status={i18n.getMessage(`transaction.status.${transactionStatus}`)} />
      </td>
      <td className={'transaction-from-to'}>
        {isCryptoProvider(transaction.transfer_type) ? (
          <p className={'iban-text'}>{formatAccountNumber(transaction?.counterparty?.crypto_wallet)}</p>
        ) : transaction.type === 'OUTGOING' ? (
          <>
            <p>{transaction?.recipient_name}</p>
            <p className={'iban-text'}>
              {formatAccountNumber(transaction.counterparty?.iban || transaction.counterparty?.number)}
            </p>
          </>
        ) : (
          <>
            <p>{transaction?.sender_name}</p>
            <p className={'iban-text'}>
              {formatAccountNumber(transaction.counterparty?.iban || transaction.counterparty?.number)}
            </p>
          </>
        )}
      </td>
      <td className={'transaction-purpose'}>
        {isCryptoTransactions ? formatAccountNumber(transaction.crypto_hash) : transaction.comment}
      </td>
      <td className={`transaction-amount-commission ${transaction?.type}`}>
        <p className={'amount'}>{amount}</p>
        {transaction.transaction_type !== 'COMMISSION' && (
          <p className={'commission-total'}>
            {`- ${transaction.totalCommissionDetails?.displayAmount || amountFormattedValue(transaction.total_commissions || 0, 2)}`}
          </p>
        )}
      </td>
      <td className={'transaction-exclamation'}>
        <ActionButton tooltipText={i18n.getMessage('popUp.paymentDetails')} onClick={onInfoClick} />
      </td>
    </tr>
  );
};

TransactionItem.propTypes = {
  transaction: PropTypes.object.isRequired,
  onInfoClick: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  isCryptoTransactions: PropTypes.bool
};
